/**
 * 🏆 EXCELÊNCIA SERVICE
 *
 * Serviço dedicado ao módulo de Governança de Processos (Excelência).
 *
 * Responsabilidades:
 *  - KPIs agregados da organização
 *  - Publicação de blueprints
 *  - Versionamento (cadeia parent → child)
 *  - Toggle de padrão corporativo
 *  - Geração de workflow / mapa a partir de blueprint
 *
 * Delega CRUD e geração ao processBlueprintService.
 * Não duplica lógica; adiciona camada de governança.
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../../../lib/supabaseClient';
import {
  listBlueprints,
  getBlueprintById,
  createBlueprint,
  updateBlueprint,
  cloneBlueprint as cloneBlueprintBase,
  generateWorkflowFromMap,
  generateMapFromWorkflow,
  getBlueprintUsageCount,
  validateStatusTransition,
  extractStagesFromMap,
} from './processBlueprintService';
import {
  listVersions,
  getUsageCounts,
  getVersionById,
} from './blueprintVersionUsageService';
import {
  buildBlueprintSnapshotSummary,
  buildBlueprintStageSnapshot,
  resolveStageEngineering,
} from './blueprintSnapshotMetrics';
import { logGovernanceAction } from './blueprintLogService';
import { assertGovernancePermission, type GovernanceAction } from './governancePermissions';
import { sha256 } from '../../../lib/hashUtils';
import { structuredLogger } from '../../../lib/structuredLogger';
import {
  ensureWorkflowRoles,
  humanizeWorkflowRoleName,
  normalizeWorkflowRoleKey,
  userHasWorkflowRole,
} from '../../shared/services/workflowRoleService';
import type {
  ProcessBlueprint,
  ExcelenciaKPIs,
  MapToWorkflowResult,
  WorkflowToMapResult,
  BlueprintCategory,
  BlueprintStage,
  VersionBumpType,
  GovernanceImpactMetrics,
} from '../../../types/processBlueprint';

// ============================================================================
// HELPERS — Versionamento Semântico
// ============================================================================

const DEFAULT_MAJOR = 1;
const DEFAULT_MINOR = 0;
const DEFAULT_PATCH = 0;

function toSafePart(value?: number | null, fallback = 0): number {
  return Number.isFinite(value) ? Number(value) : fallback;
}

function buildSemverFromParts(major?: number | null, minor?: number | null, patch?: number | null): string {
  const safeMajor = toSafePart(major, DEFAULT_MAJOR);
  const safeMinor = toSafePart(minor, DEFAULT_MINOR);
  const safePatch = toSafePart(patch, DEFAULT_PATCH);
  return `${safeMajor}.${safeMinor}.${safePatch}`;
}

function getBlueprintSemver(bp: ProcessBlueprint): string {
  return bp.versionSemver ?? buildSemverFromParts(bp.versionMajor, bp.versionMinor, bp.versionPatch);
}

function bumpSemver(bp: ProcessBlueprint, bump: VersionBumpType): {
  versionMajor: number;
  versionMinor: number;
  versionPatch: number;
  versionSemver: string;
} {
  let major = bp.versionMajor ?? DEFAULT_MAJOR;
  let minor = bp.versionMinor ?? DEFAULT_MINOR;
  let patch = bp.versionPatch ?? DEFAULT_PATCH;

  switch (bump) {
    case 'major':
      major += 1;
      minor = 0;
      patch = 0;
      break;
    case 'minor':
      minor += 1;
      patch = 0;
      break;
    default:
      patch += 1;
  }

  return {
    versionMajor: major,
    versionMinor: minor,
    versionPatch: patch,
    versionSemver: buildSemverFromParts(major, minor, patch),
  };
}

function enforcePermission(action: GovernanceAction, role?: string | null): void {
  if (!role) return; // Mantém compatibilidade; passa role para aplicar bloqueio real
  assertGovernancePermission(action, role);
}

/**
 * Cria um snapshot completo do estado atual do blueprint.
 * Este JSON é gravado como snapshot imutável em process_blueprint_versions.
 */
function buildBlueprintSnapshot(bp: ProcessBlueprint): Record<string, unknown> {
  const stages = bp.stages.map((stage) => buildBlueprintStageSnapshot(stage));
  const summary = buildBlueprintSnapshotSummary(stages);

  return {
    name: bp.name,
    description: bp.description ?? null,
    category: bp.category,
    sourceType: bp.sourceType,
    sourceId: bp.sourceId ?? null,
    status: bp.status,
    stages,
    ...summary,
    metrics: {
      ...summary,
    },
    tra: summary.tra_standard,
    errorPercent: summary.expected_error_percent,
    generatedTemplateId: bp.generatedTemplateId ?? null,
    isCorporateStandard: bp.isCorporateStandard,
    parentBlueprintId: bp.parentBlueprintId ?? null,
    snapshotDate: new Date().toISOString(),
  };
}

// ============================================================================
// KPIs
// ============================================================================

/**
 * Retorna métricas agregadas para o painel de governança.
 * Usa SQL COUNT via Supabase apenas com SELECT id para evitar transferências desnecessárias.
 *
 * Contadores reais:
 *  - Total           = count(*)
 *  - Publicados      = status = 'published'
 *  - Rascunhos       = status = 'draft'
 *  - Padrões         = status = 'standard'
 *  - Usados em proj. = DISTINCT blueprint_id na tabela process_blueprint_usage
 */
export async function getExcelenciaKPIs(organizationId: string): Promise<ExcelenciaKPIs> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  // Dispara todas as contagens em paralelo
  const [
    totalRes,
    publishedRes,
    draftsRes,
    standardsRes,
    corporateRes,
    fromMapsRes,
    fromWorkflowsRes,
    lastUpdatedRes,
    usedRes,
  ] = await Promise.all([
    // Total
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId),
    // Publicados
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('status', 'published'),
    // Rascunhos
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('status', 'draft'),
    // Padrões
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('status', 'standard'),
    // Corporate standards (flag ou status)
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('is_corporate_standard', true),
    // Originados de mapas
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('source_type', 'map'),
    // Originados de workflows
    supabase
      .from('process_blueprints')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('source_type', 'workflow'),
    // Última atualização
    supabase
      .from('process_blueprints')
      .select('updated_at')
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    // Blueprints usados em projetos (distinct via tabela de uso)
    supabase
      .from('process_blueprint_usage')
      .select('blueprint_id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .then((res) => res, () => ({ data: null, error: null, count: 0 } as any)),
  ]);

  return {
    totalBlueprints: totalRes.count ?? 0,
    published: publishedRes.count ?? 0,
    drafts: draftsRes.count ?? 0,
    standards: standardsRes.count ?? 0,
    corporateStandards: corporateRes.count ?? 0,
    fromMaps: fromMapsRes.count ?? 0,
    fromWorkflows: fromWorkflowsRes.count ?? 0,
    lastUpdated: lastUpdatedRes.data?.updated_at ?? null,
    usedInProjects: usedRes.count ?? 0,
  };
}

/**
 * Métricas de impacto organizacional para o painel executivo.
 * Baseada na view process_blueprint_org_metrics (agregada por organização).
 */
export async function getGovernanceImpactMetrics(
  organizationId: string,
): Promise<GovernanceImpactMetrics> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  let data: any = null;
  try {
    const result = await supabase
      .from('process_blueprint_org_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle();
    if (!result.error) data = result.data;
  } catch {
    // View may not exist yet
  }

  if (!data) {
    return {
      totalBlueprints: 0,
      published: 0,
      drafts: 0,
      standards: 0,
      outdatedUsages: 0,
      totalUsages: 0,
      projectsUsing: 0,
      percentProjectsStandard: 0,
    };
  }

  return {
    totalBlueprints: data.total_blueprints ?? 0,
    published: data.published ?? 0,
    drafts: data.drafts ?? 0,
    standards: data.standards ?? 0,
    outdatedUsages: data.outdated_usages ?? 0,
    totalUsages: data.total_usages ?? 0,
    projectsUsing: data.projects_using ?? 0,
    percentProjectsStandard: data.percent_projects_standard ?? 0,
  };
}

// ============================================================================
// SINCRONIZAÇÃO BLUEPRINT → WORKFLOW TEMPLATE (para Execução)
// ============================================================================

/**
 * Sincroniza um blueprint publicado com workflow_templates_v9 + workflow_stages_v9.
 * Se o blueprint já possui generatedTemplateId, atualiza o template existente.
 * Caso contrário, cria um novo template e vincula ao blueprint.
 *
 * Isso garante que a tela de Execução enxerga o blueprint publicado.
 */
/**
 * Desativa um workflow template (is_active=false).
 * Usado quando um blueprint é arquivado.
 */
async function deactivateTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('workflow_templates_v9')
    .update({ is_active: false })
    .eq('id', templateId);

  if (error) {
    console.warn('[deactivateTemplate] Failed to deactivate template:', error.message);
  }
}

// ============================================================================
// PUBLICAÇÃO
// ============================================================================

/**
 * Publica um blueprint (muda status para 'published').
 * Valida que a transição de status é permitida.
 * Valida que o blueprint possui ao menos 1 stage.
 * Cria snapshot da versão atual (incrementa patch) antes de publicar.
 * Retorna o blueprint atualizado.
 */
export async function publishBlueprint(blueprintId: string, publishedBy?: string, actorRole?: string): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  enforcePermission('publish', actorRole);

  const bp = await getBlueprintById(blueprintId);
  if (!bp) throw new Error('Blueprint não encontrado');

  validateStatusTransition(bp.status, 'published');

  // Validação: blueprint deve ter ao menos 1 etapa
  if (!bp.stages || bp.stages.length === 0) {
    throw new Error('Não é possível publicar um blueprint sem etapas. Adicione ao menos 1 etapa antes de publicar.');
  }

  // Validação: blueprint deve ter descrição
  if (!bp.description?.trim()) {
    throw new Error('Não é possível publicar sem descrição. Adicione uma descrição ao blueprint antes de publicar.');
  }

  const currentSemver = getBlueprintSemver(bp);

  // Bump patch version ao publicar
  const bumped = bumpSemver(bp, 'patch');
  const publishSnapshot = buildBlueprintSnapshot(bp);
  const publishHash = await sha256(publishSnapshot).catch(() => null);
  const { error: versionError } = await supabase
    .from('process_blueprint_versions')
    .insert({
      blueprint_id: bp.id,
      organization_id: bp.organizationId,
      version: bumped.versionMajor * 10000 + bumped.versionMinor * 100 + bumped.versionPatch,
      version_semver: bumped.versionSemver,
      version_major: bumped.versionMajor,
      version_minor: bumped.versionMinor,
      version_patch: bumped.versionPatch,
      change_type: 'patch',
      change_summary: 'Publicação do blueprint',
      snapshot: publishSnapshot,
      snapshot_hash: publishHash,
    });

  if (versionError) {
    structuredLogger.warn('[publishBlueprint] Failed to insert version snapshot', {
      operation: 'publishBlueprint',
      organizationId: bp.organizationId,
      blueprintId: bp.id,
    }, versionError);
  }

  const result = await updateBlueprint(blueprintId, { status: 'published' });

  // Log de governança
  logGovernanceAction({
    blueprintId: bp.id,
    organizationId: bp.organizationId,
    action: 'published',
    performedBy: publishedBy,
    details: { previousStatus: bp.status, versionSemver: currentSemver, newSemver: bumped.versionSemver },
  });

  return result;
}

// ============================================================================
// ARQUIVAMENTO
// ============================================================================

/**
 * Arquiva um blueprint (muda status para 'archived').
 * Valida que a transição de status é permitida.
 * Retorna o blueprint atualizado.
 */
export async function archiveBlueprint(blueprintId: string, actorRole?: string): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  enforcePermission('archive', actorRole);

  const bp = await getBlueprintById(blueprintId);
  if (!bp) throw new Error('Blueprint não encontrado');

  validateStatusTransition(bp.status, 'archived');

  const result = await updateBlueprint(blueprintId, { status: 'archived' });

  // Desativar workflow template associado para sumir da tela de Execução
  if (result.generatedTemplateId) {
    await deactivateTemplate(result.generatedTemplateId);
  }

  // Log de governança
  logGovernanceAction({
    blueprintId: bp.id,
    organizationId: bp.organizationId,
    action: 'archived',
    details: { previousStatus: bp.status, versionSemver: getBlueprintSemver(bp) },
  });

  return result;
}

// ============================================================================
// CLONAGEM
// ============================================================================

/**
 * Clona um blueprint existente com novo nome.
 * Wrapper sobre processBlueprintService.cloneBlueprint.
 */
export async function cloneBlueprint(
  blueprintId: string,
  organizationId: string,
  newName?: string,
): Promise<ProcessBlueprint> {
  const result = await cloneBlueprintBase(blueprintId, organizationId, newName);

  // Log de governança
  logGovernanceAction({
    blueprintId: result.id,
    organizationId: result.organizationId,
    action: 'cloned',
    details: { clonedFrom: blueprintId, name: result.name },
  });

  return result;
}

// ============================================================================
// VERSIONAMENTO
// ============================================================================

/**
 * Cria uma nova versão do blueprint.
 *
 * Fluxo CORRETO:
 *  1. Carrega o blueprint original
 *  2. Persiste snapshot COMPLETO da versão atual em process_blueprint_versions
 *  3. Incrementa a versão semântica no MESMO blueprint (não cria novo)
 *  4. Retorna o blueprint atualizado com a nova versão
 *
 * NÃO cria novo blueprint. Apenas grava snapshot + bumpa versão.
 */
export async function createVersion(
  blueprintId: string,
  changeSummary?: string,
  createdBy?: string,
  bumpType: VersionBumpType = 'patch',
): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const original = await getBlueprintById(blueprintId);
  if (!original) throw new Error('Blueprint não encontrado');

  const originalSemver = getBlueprintSemver(original);
  const bumped = bumpSemver(original, bumpType);

  // 1. Gravar snapshot imutável da versão atual
  const snapshotData = buildBlueprintSnapshot(original);
  const snapshotHash = await sha256(snapshotData).catch(() => undefined);
  const { error: versionError } = await supabase
    .from('process_blueprint_versions')
    .insert({
      blueprint_id: original.id,
      organization_id: original.organizationId,
      version: bumped.versionMajor * 10000 + bumped.versionMinor * 100 + bumped.versionPatch,
      version_semver: bumped.versionSemver,
      version_major: bumped.versionMajor,
      version_minor: bumped.versionMinor,
      version_patch: bumped.versionPatch,
      change_type: bumpType,
      change_summary: changeSummary ?? 'Nova versão',
      snapshot: snapshotData,
      snapshot_hash: snapshotHash ?? null,
    });

  if (versionError) {
    console.warn('[createVersion] Failed to insert version snapshot:', versionError.message);
  }

  // 2. Log de governança
  logGovernanceAction({
    blueprintId: original.id,
    organizationId: original.organizationId,
    action: 'versioned',
    performedBy: createdBy,
    details: {
      previousSemver: originalSemver,
      newSemver: bumped.versionSemver,
      changeType: bumpType,
      changeSummary,
    },
  });

  // 3. Recarregar o blueprint (reflect updated version from DB trigger or subsequent reads)
  const refreshed = await getBlueprintById(blueprintId);
  return refreshed ?? original;
}

// ============================================================================
// PADRÃO CORPORATIVO
// ============================================================================

/**
 * Marca um blueprint como padrão corporativo (status = 'standard').
 *
 * Regras:
 *  1. Apenas blueprint com status 'published' pode virar padrão
 *  2. Apenas 1 padrão por organização por categoria
 *  3. Se já existir outro padrão na mesma categoria, ele é rebaixado para 'published'
 */
export async function setAsStandard(blueprintId: string, actorRole?: string): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  enforcePermission('setStandard', actorRole);

  const bp = await getBlueprintById(blueprintId);
  if (!bp) throw new Error('Blueprint não encontrado');

  // Regra 1: apenas published pode virar standard
  if (bp.status !== 'published') {
    throw new Error(
      `Apenas blueprints publicados podem se tornar padrão. Status atual: ${bp.status}`,
    );
  }

  // Regra 2+3: verificar se já existe outro padrão na mesma categoria
  const allBlueprints = await listBlueprints(bp.organizationId);
  const existingStandard = allBlueprints.find(
    b => b.id !== bp.id && b.status === 'standard' && b.category === bp.category,
  );

  // Se já existe um padrão na mesma categoria, rebaixar para 'published'
  if (existingStandard) {
    await updateBlueprint(existingStandard.id, {
      status: 'published',
      isCorporateStandard: false,
    });
  }

  const result = await updateBlueprint(blueprintId, {
    status: 'standard',
    isCorporateStandard: true,
  });

  // Log de governança
  logGovernanceAction({
    blueprintId: bp.id,
    organizationId: bp.organizationId,
    action: 'set_standard',
    details: {
      previousStatus: bp.status,
      replacedStandardId: existingStandard?.id ?? null,
      category: bp.category,
    },
  });

  return result;
}

/**
 * Remove o status de padrão corporativo (standard → published).
 */
export async function removeStandard(blueprintId: string, actorRole?: string): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const bp = await getBlueprintById(blueprintId);
  if (!bp) throw new Error('Blueprint não encontrado');

  if (bp.status !== 'standard') {
    throw new Error('Blueprint não é um padrão corporativo');
  }

  enforcePermission('setStandard', actorRole);

  const result = await updateBlueprint(blueprintId, {
    status: 'published',
    isCorporateStandard: false,
  });

  // Log de governança
  logGovernanceAction({
    blueprintId: bp.id,
    organizationId: bp.organizationId,
    action: 'removed_standard',
    details: { category: bp.category },
  });

  return result;
}

/**
 * Marca ou desmarca um blueprint como padrão corporativo.
 * Se isCorporate=true, promove para 'standard' (com validação).
 * Se isCorporate=false, rebaixa para 'published'.
 */
export async function toggleCorporateStandard(
  blueprintId: string,
  isCorporate: boolean,
  actorRole?: string,
): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  if (isCorporate) {
    return setAsStandard(blueprintId, actorRole);
  } else {
    return removeStandard(blueprintId, actorRole);
  }
}

// ============================================================================
// GERAÇÃO A PARTIR DE BLUEPRINT
// ============================================================================

/**
 * Gera um workflow V9 (template + stages) a partir de um blueprint existente.
 *
 * Delega inteiramente ao processBlueprintService.generateWorkflowFromMap
 * Requer que o blueprint tenha stages extraídos.
 */
export async function generateWorkflowFromBlueprint(
  blueprintId: string,
  createdBy?: string,
): Promise<MapToWorkflowResult> {
  const blueprint = await getBlueprintById(blueprintId);
  if (!blueprint) throw new Error('Blueprint não encontrado');
  if (blueprint.stages.length === 0) {
    throw new Error('Blueprint não possui etapas para gerar workflow');
  }

  // Usar a função de geração existente passando os dados do blueprint
  // O generateWorkflowFromMap já cria blueprint internamente; aqui reutilizamos
  // a lógica de criação de template + stages diretamente via Supabase
  const { data: template, error: templateError } = await supabase
    .from('workflow_templates_v9')
    .insert({
      organization_id: blueprint.organizationId,
      name: `[Draft] ${blueprint.name}`,
      description: `Pré-workflow gerado a partir do blueprint "${blueprint.name}"`,
      category: 'processo',
      is_active: false,
      version: 1,
      require_sequential_completion: true,
      auto_assign_strategy: 'manual',
      created_by: createdBy ?? null,
      metadata: {
        blueprint_id: blueprint.id,
        generated_at: new Date().toISOString(),
      },
    })
    .select('id')
    .single();

  if (templateError) throw templateError;

  const stageInserts = blueprint.stages.map(stage => {
    const stageEngineering = resolveStageEngineering(stage);
    return {
      template_id: template.id,
      organization_id: blueprint.organizationId,
      name: stage.name,
      description: stage.description ?? null,
      stage_order: stage.order,
      sla_hours: stage.slaHours ?? null,
      sla_business_hours: false,
      warning_threshold_percent: stageEngineering.warningThresholdPercent,
      require_approval: stageEngineering.requireApproval,
      can_skip: false,
      default_assignee_role: (stage.metadata as any)?.responsavel ?? null,
      wip_limit: stageEngineering.wipLimit,
      metadata: {
        source_node_id: stage.sourceNodeId,
        tp_standard: stageEngineering.tpStandard,
        tra_standard: stageEngineering.traStandard,
        expected_error_percent: stageEngineering.expectedErrorPercent,
        correction_time_standard: stageEngineering.correctionTimeStandard,
        warning_threshold_percent: stageEngineering.warningThresholdPercent,
        wip_limit: stageEngineering.wipLimit,
        require_approval: stageEngineering.requireApproval,
      },
    };
  });

  const { error: stagesError } = await supabase
    .from('workflow_stages_v9')
    .insert(stageInserts);

  if (stagesError) throw stagesError;

  // Atualizar blueprint com referência ao template
  const updated = await updateBlueprint(blueprint.id, {
    generatedTemplateId: template.id,
    status: 'published',
  });

  return {
    blueprint: updated,
    templateId: template.id,
    stagesCreated: blueprint.stages.length,
  };
}

/**
 * Gera esqueleto de mapa VSM a partir de um blueprint existente.
 *
 * Cria cards e conexões sequenciais baseados nos stages do blueprint.
 */
export async function generateMapFromBlueprint(
  blueprintId: string,
): Promise<WorkflowToMapResult> {
  const blueprint = await getBlueprintById(blueprintId);
  if (!blueprint) throw new Error('Blueprint não encontrado');
  if (blueprint.stages.length === 0) {
    throw new Error('Blueprint não possui etapas para gerar mapa');
  }

  const CARD_SPACING_X = 320;
  const CARD_START_X = 100;
  const CARD_Y = 200;

  const initialCardId = crypto.randomUUID();
  const finalCardId = crypto.randomUUID();

  const cards: Array<{
    id: string;
    nome_fluxo?: string;
    atividade: string;
    posicao: { x: number; y: number };
    tipo: string;
    tp?: number;
    lt?: number;
  }> = [];

  const connections: Array<{ sourceId: string; targetId: string }> = [];

  // Card inicial
  cards.push({
    id: initialCardId,
    nome_fluxo: blueprint.name,
    atividade: 'Início',
    posicao: { x: CARD_START_X, y: CARD_Y },
    tipo: 'card_inicial',
  });

  let prevCardId = initialCardId;

  blueprint.stages.forEach((stage, index) => {
    const cardId = crypto.randomUUID();
    cards.push({
      id: cardId,
      atividade: stage.name,
      posicao: { x: CARD_START_X + (index + 1) * CARD_SPACING_X, y: CARD_Y },
      tipo: 'atividade',
      tp: stage.slaHours ? stage.slaHours * 60 : undefined,
      lt: stage.slaHours ? stage.slaHours * 60 : undefined,
    });

    connections.push({ sourceId: prevCardId, targetId: cardId });
    prevCardId = cardId;
  });

  // Card final
  cards.push({
    id: finalCardId,
    atividade: 'Fim',
    posicao: { x: CARD_START_X + (blueprint.stages.length + 1) * CARD_SPACING_X, y: CARD_Y },
    tipo: 'card_final',
  });

  connections.push({ sourceId: prevCardId, targetId: finalCardId });

  const mapData = {
    cards,
    connections,
    generatedFromWorkflow: true as const,
  };

  // Atualizar blueprint com mapa gerado
  const updated = await updateBlueprint(blueprint.id, {
    generatedMapData: mapData,
  });

  return {
    blueprint: updated,
    mapData,
  };
}

// ============================================================================
// LISTAGEM DEDICADA
// ============================================================================

/**
 * Lista blueprints por organização.
 * Wrapper sobre processBlueprintService.listBlueprints.
 */
export async function getBlueprintsByOrganization(
  organizationId: string,
): Promise<ProcessBlueprint[]> {
  return listBlueprints(organizationId);
}

// ============================================================================
// CRIAÇÃO MANUAL DE BLUEPRINT
// ============================================================================

/**
 * Cria um blueprint manual (sem mapa ou workflow de origem).
 * Usado quando o gestor quer definir um processo do zero.
 */
export async function createManualBlueprint(params: {
  organizationId: string;
  name: string;
  description?: string;
  category?: BlueprintCategory;
  stages?: BlueprintStage[];
  projectId?: string | null;
  createdBy?: string;
  actorRole?: string;
}): Promise<ProcessBlueprint> {
  const { organizationId, name, description, category, stages, projectId, createdBy, actorRole } = params;

  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!name?.trim()) throw new Error('Nome é obrigatório');

  enforcePermission('create', actorRole);

  const blueprintStages = (stages ?? []).map((s, i) => ({
    order: i + 1,
    name: s.name,
    description: s.description,
    slaHours: s.slaHours,
    role: s.role,
    checklist: s.checklist,
    formFields: s.formFields,
    rules: s.rules,
    notifications: s.notifications,
    sourceNodeId: s.sourceNodeId,
    metadata: s.metadata,
    instruction: s.instruction,
    completionType: s.completionType,
    exitOptions: s.exitOptions,
  }));

  return createBlueprint({
    organizationId,
    projectId: projectId ?? null,
    name: name.trim(),
    description: description?.trim(),
    sourceType: 'manual',
    sourceId: null,
    stages: blueprintStages,
    status: 'draft',
    category: category ?? 'geral',
    createdBy,
  }).then(bp => {
    // Log de governança
    logGovernanceAction({
      blueprintId: bp.id,
      organizationId: bp.organizationId,
      action: 'created',
      performedBy: createdBy,
      details: { name: bp.name, sourceType: 'manual', category: bp.category },
    });
    return bp;
  });
}

// ============================================================================
// FONTES DE IMPORTAÇÃO — Listar Mapas e Workflows disponíveis
// ============================================================================

/** Item resumido de um mapa (projeto) disponível para importação */
export interface MapSourceItem {
  id: string;
  name: string;
  updatedAt: string;
  cardCount: number;
}

/** Item resumido de um workflow template disponível para importação */
export interface WorkflowSourceItem {
  id: string;
  name: string;
  description: string | null;
  stageCount: number;
  updatedAt: string;
}

/**
 * Lista projetos (mapas VSM) do usuário que possuem cards de atividade.
 * Esses projetos podem ser importados como blueprint.
 */
export async function listAvailableMaps(userId: string): Promise<MapSourceItem[]> {
  if (!userId) return [];

  const { data, error } = await supabase
    .from('projects')
    .select('id, name, data, updated_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  if (!data) return [];

  return data
    .filter((p: any) => {
      // Verificar se o projeto tem cards de atividade
      const projectData = p.data;
      if (!projectData) return false;
      const cards = projectData.estado_atual?.cards ?? projectData.estado_atual?.sheets?.[0]?.cards ?? [];
      return cards.some((c: any) => c.tipo === 'atividade' && c.status === 'ativo');
    })
    .map((p: any) => {
      const projectData = p.data;
      const cards = projectData.estado_atual?.cards ?? projectData.estado_atual?.sheets?.[0]?.cards ?? [];
      const activityCards = cards.filter((c: any) => c.tipo === 'atividade' && c.status === 'ativo');
      return {
        id: p.id,
        name: p.name || projectData.nome_projeto || 'Sem nome',
        updatedAt: p.updated_at,
        cardCount: activityCards.length,
      };
    });
}

/**
 * Lista workflow templates V9 ativos da organização.
 * Esses templates podem ser importados como blueprint.
 */
export async function listAvailableWorkflows(organizationId: string): Promise<WorkflowSourceItem[]> {
  if (!organizationId) return [];

  const { data, error } = await supabase
    .from('workflow_templates_v9')
    .select('id, name, description, updated_at')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  if (!data) return [];

  // Buscar contagem de stages para cada template
  const templateIds = data.map((t: any) => t.id);
  const { data: stagesData } = await supabase
    .from('workflow_stages_v9')
    .select('template_id')
    .in('template_id', templateIds);

  const stageCountMap = new Map<string, number>();
  (stagesData ?? []).forEach((s: any) => {
    stageCountMap.set(s.template_id, (stageCountMap.get(s.template_id) ?? 0) + 1);
  });

  return data.map((t: any) => ({
    id: t.id,
    name: t.name,
    description: t.description ?? null,
    stageCount: stageCountMap.get(t.id) ?? 0,
    updatedAt: t.updated_at,
  }));
}

// ============================================================================
// CRIAÇÃO A PARTIR DE IMPORTAÇÃO
// ============================================================================

/**
 * Cria um blueprint importando dados de um mapa VSM existente.
 *
 * Fluxo:
 *  1. Carrega o projeto (mapa) do banco
 *  2. Extrai etapas usando extractStagesFromMap
 *  3. Cria o blueprint com sourceType='map', sourceId=projectId
 *  4. Retorna o blueprint criado (status=draft)
 */
export async function createBlueprintFromMap(params: {
  organizationId: string;
  name: string;
  description?: string;
  category?: BlueprintCategory;
  mapId: string;
  createdBy?: string;
  actorRole?: string;
}): Promise<ProcessBlueprint> {
  const { organizationId, name, description, category, mapId, createdBy, actorRole } = params;

  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!name?.trim()) throw new Error('Nome é obrigatório');
  if (!mapId) throw new Error('ID do mapa é obrigatório');

  enforcePermission('create', actorRole);

  // 1. Carregar projeto
  const { data: projectRow, error: projectError } = await supabase
    .from('projects')
    .select('data')
    .eq('id', mapId)
    .single();

  if (projectError) throw new Error('Mapa não encontrado');
  if (!projectRow?.data) throw new Error('Dados do mapa estão vazios');

  const projectData = projectRow.data as any;
  const cards = projectData.estado_atual?.cards
    ?? projectData.estado_atual?.sheets?.[0]?.cards
    ?? [];

  // 2. Extrair stages
  const stages = extractStagesFromMap(cards);
  if (stages.length === 0) {
    throw new Error('Nenhuma atividade encontrada no mapa para importar');
  }

  // 3. Salvar snapshot do mapa nos metadados do blueprint
  const mapSnapshot = {
    cards: cards.map((c: any) => ({
      id: c.id,
      tipo: c.tipo,
      atividade: c.atividade,
      responsavel: c.responsavel,
      sistema: c.sistema,
      tp: c.tp,
      lt: c.lt,
      status: c.status,
    })),
    snapshotDate: new Date().toISOString(),
  };

  // 4. Criar blueprint
  return createBlueprint({
    organizationId,
    name: name.trim(),
    description: description?.trim() || `Importado do mapa "${name}"`,
    sourceType: 'map',
    sourceId: mapId,
    stages,
    status: 'draft',
    category: category ?? 'geral',
    createdBy,
  }).then(bp => {
    logGovernanceAction({
      blueprintId: bp.id,
      organizationId: bp.organizationId,
      action: 'created',
      performedBy: createdBy,
      details: { name: bp.name, sourceType: 'map', sourceId: mapId },
    });
    return bp;
  });
}

/**
 * Cria um blueprint importando dados de um workflow template V9.
 *
 * Fluxo:
 *  1. Carrega as stages do workflow_stages_v9
 *  2. Converte para BlueprintStages
 *  3. Cria o blueprint com sourceType='workflow', sourceId=templateId
 *  4. Retorna o blueprint criado (status=draft)
 */
export async function createBlueprintFromWorkflow(params: {
  organizationId: string;
  name: string;
  description?: string;
  category?: BlueprintCategory;
  templateId: string;
  createdBy?: string;
  actorRole?: string;
}): Promise<ProcessBlueprint> {
  const { organizationId, name, description, category, templateId, createdBy, actorRole } = params;

  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!name?.trim()) throw new Error('Nome é obrigatório');
  if (!templateId) throw new Error('ID do template é obrigatório');

  enforcePermission('create', actorRole);

  // 1. Carregar stages do template
  const { data: stagesData, error: stagesError } = await supabase
    .from('workflow_stages_v9')
    .select('*')
    .eq('template_id', templateId)
    .eq('organization_id', organizationId)
    .order('stage_order');

  if (stagesError) throw stagesError;
  if (!stagesData || stagesData.length === 0) {
    throw new Error('Nenhuma etapa encontrada no workflow template');
  }

  // 2. Converter em BlueprintStages
  const stages = stagesData.map((stage: any) => ({
    order: stage.stage_order,
    name: stage.name,
    description: stage.description ?? undefined,
    slaHours: stage.sla_hours ? Number(stage.sla_hours) : undefined,
    sourceNodeId: stage.id,
    tpStandard: stage.sla_hours ? Number(stage.sla_hours) * 60 : undefined,
    warningThresholdPercent: stage.warning_threshold_percent ?? undefined,
    wipLimit: stage.wip_limit ?? undefined,
    requireApproval: stage.require_approval ?? undefined,
    rules: stage.require_approval ? { requireApproval: true } : undefined,
    metadata: {
      default_assignee_role: stage.default_assignee_role,
      wip_limit: stage.wip_limit,
    },
  }));

  // 3. Criar blueprint
  return createBlueprint({
    organizationId,
    name: name.trim(),
    description: description?.trim() || `Importado do workflow template`,
    sourceType: 'workflow',
    sourceId: templateId,
    stages,
    status: 'draft',
    category: category ?? 'geral',
    createdBy,
  }).then(bp => {
    logGovernanceAction({
      blueprintId: bp.id,
      organizationId: bp.organizationId,
      action: 'created',
      performedBy: createdBy,
      details: { name: bp.name, sourceType: 'workflow', sourceId: templateId },
    });
    return bp;
  });
}

// ============================================================================
// RE-EXPORTS — Version & Usage (conveniência)
// ============================================================================

export {
  listVersions,
  getUsageCounts,
} from './blueprintVersionUsageService';

export {
  listUsageByBlueprint,
  listUsageByProject,
  listAllUsage,
  linkBlueprintToProject,
  unlinkBlueprintFromProject,
} from './blueprintVersionUsageService';

export {
  getBlueprintUsageCount,
  validateStatusTransition,
} from './processBlueprintService';

// ============================================================================
// EDIÇÃO COM VERSIONAMENTO AUTOMÁTICO
// ============================================================================

/**
 * Edita um blueprint existente.
 *
 * Regra de negócio: ao alterar campos de conteúdo (name, description, stages, category),
 * cria automaticamente um snapshot da versão atual ANTES de aplicar as alterações.
 * Isso garante rastreabilidade completa de todas as mudanças.
 *
 * Campos que NÃO disparam versionamento: status, isCorporateStandard,
 * generatedTemplateId, generatedMapData (metadados operacionais).
 */
export async function editBlueprint(
  blueprintId: string,
  changes: {
    name?: string;
    description?: string;
    stages?: BlueprintStage[];
    category?: string;
  },
  editedBy?: string,
  changeSummary?: string,
  bumpType: VersionBumpType = 'patch',
): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const original = await getBlueprintById(blueprintId);
  if (!original) throw new Error('Blueprint não encontrado');

  // Verificar se há alterações de conteúdo que requerem versionamento
  const hasContentChanges =
    (changes.name !== undefined && changes.name !== original.name) ||
    (changes.description !== undefined && changes.description !== original.description) ||
    (changes.stages !== undefined) ||
    (changes.category !== undefined && changes.category !== original.category);

  // Bloqueio de edição de blueprint publicado: grava snapshot da versão atual + aplica mudanças como nova versão
  if ((original.status === 'published' || original.status === 'standard') && hasContentChanges) {
    // 1. Gravar snapshot da versão publicada antes de modificar
    const bumped = bumpSemver(original, bumpType === 'major' ? 'major' : 'minor');
    const { error: versionError } = await supabase
      .from('process_blueprint_versions')
      .insert({
        blueprint_id: original.id,
        organization_id: original.organizationId,
        version: bumped.versionMajor * 10000 + bumped.versionMinor * 100 + bumped.versionPatch,
        version_semver: bumped.versionSemver,
        version_major: bumped.versionMajor,
        version_minor: bumped.versionMinor,
        version_patch: bumped.versionPatch,
        change_type: bumpType === 'major' ? 'major' : 'minor',
        change_summary: changeSummary ?? 'Revisão de blueprint publicado',
        snapshot: buildBlueprintSnapshot(original),
      });

    if (versionError) {
      console.warn('[editBlueprint] Failed to insert version snapshot for published edit:', versionError.message);
    }

    // 2. Aplicar mudanças e reverter para draft
    const payload: Record<string, any> = { status: 'draft' };
    if (changes.name !== undefined) payload.name = changes.name;
    if (changes.description !== undefined) payload.description = changes.description;
    if (changes.category !== undefined) payload.category = changes.category;
    if (changes.stages !== undefined) {
      payload.stages = changes.stages.map((s, i) => ({
        order: s.order ?? i + 1,
        name: s.name,
        description: s.description,
        slaHours: s.slaHours,
        role: s.role,
        checklist: s.checklist,
        formFields: s.formFields,
        rules: s.rules,
        notifications: s.notifications,
        sourceNodeId: s.sourceNodeId,
        metadata: s.metadata,
        instruction: s.instruction,
        completionType: s.completionType,
        exitOptions: s.exitOptions,
      }));
    }

    const updatedDraft = await updateBlueprint(original.id, payload);

    logGovernanceAction({
      blueprintId: updatedDraft.id,
      organizationId: updatedDraft.organizationId,
      action: 'edited',
      performedBy: editedBy,
      details: {
        changeSummary: changeSummary ?? 'Edição gera nova versão draft',
        previousSemver: original.versionSemver,
        newSemver: bumped.versionSemver,
        changedFields: Object.keys(changes),
        previousStatus: original.status,
      },
    });

    const refreshed = (await getBlueprintById(original.id)) ?? updatedDraft;
    return refreshed;
  }

  if (hasContentChanges) {

    const payload: Record<string, any> = {};

    if (changes.name !== undefined) payload.name = changes.name;
    if (changes.description !== undefined) payload.description = changes.description;
    if (changes.category !== undefined) payload.category = changes.category;
    if (changes.stages !== undefined) {
      payload.stages = changes.stages.map((s, i) => ({
        order: s.order ?? i + 1,
        name: s.name,
        description: s.description,
        slaHours: s.slaHours,
        role: s.role,
        checklist: s.checklist,
        formFields: s.formFields,
        rules: s.rules,
        notifications: s.notifications,
        sourceNodeId: s.sourceNodeId,
        metadata: s.metadata,
        instruction: s.instruction,
        completionType: s.completionType,
        exitOptions: s.exitOptions,
      }));
    }

    const updated = await updateBlueprint(blueprintId, payload);
    const summary = changeSummary ?? 'Edição de conteúdo';

    // Bump version: insert new version record with snapshot COMPLETO
    const bumped = bumpSemver(original, bumpType);
    const { error: versionError } = await supabase
      .from('process_blueprint_versions')
      .insert({
        blueprint_id: original.id,
        organization_id: original.organizationId,
        version: bumped.versionMajor * 10000 + bumped.versionMinor * 100 + bumped.versionPatch,
        version_semver: bumped.versionSemver,
        version_major: bumped.versionMajor,
        version_minor: bumped.versionMinor,
        version_patch: bumped.versionPatch,
        change_type: bumpType,
        change_summary: summary,
        snapshot: buildBlueprintSnapshot(original),
        snapshot_hash: await sha256(buildBlueprintSnapshot(original)).catch(() => null),
      });

    if (versionError) {
      console.warn('[editBlueprint] Failed to insert version bump:', versionError.message);
    }

    const refreshed = (await getBlueprintById(blueprintId)) ?? updated;

    logGovernanceAction({
      blueprintId: refreshed.id,
      organizationId: refreshed.organizationId,
      action: 'edited',
      performedBy: editedBy,
      details: {
        changeSummary: summary,
        previousSemver: original.versionSemver,
        newSemver: refreshed.versionSemver,
        changedFields: Object.keys(changes),
      },
    });

    return refreshed;
  }

  // Sem alteração de conteúdo, atualizar sem incrementar versão
  return updateBlueprint(blueprintId, changes as Record<string, any>);
}

// ============================================================================
// REATIVAR BLUEPRINT ARQUIVADO
// ============================================================================

/**
 * Reativa um blueprint arquivado (archived → draft).
 */
export async function reactivateBlueprint(blueprintId: string): Promise<ProcessBlueprint> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const bp = await getBlueprintById(blueprintId);
  if (!bp) throw new Error('Blueprint não encontrado');

  validateStatusTransition(bp.status, 'draft');

  const result = await updateBlueprint(blueprintId, { status: 'draft' });

  // Log de governança
  logGovernanceAction({
    blueprintId: bp.id,
    organizationId: bp.organizationId,
    action: 'reactivated',
    details: { previousStatus: bp.status },
  });

  return result;
}

// ============================================================================
// CRIAR WORKFLOW INSTANCE A PARTIR DE BLUEPRINT PUBLICADO
// ============================================================================

export interface BlueprintInstanceResult {
  instanceId: string;
  templateId: string;
  referenceCode: string;
  tasksCreated: number;
  firstTaskId?: string;
  sla?: { totalHours?: number } | null;
  status: 'active';
  blueprintId: string;
  versionSemver: string;
}

/**
 * Cria uma instância de workflow completa a partir de um blueprint publicado.
 *
 * Fluxo:
 *  1. Valida que o blueprint está publicado ou é padrão (standard)
 *  2. Cria workflow_template_v9 a partir dos stages do blueprint (se não existir)
 *  3. Cria workflow_instance_v9 com referência ao blueprint
 *  4. Cria workflow_tasks_v9 para cada stage (com SLA, role, checklist em metadata)
 *  5. Registra uso em process_blueprint_usage
 *  6. Retorna resultado completo com rastreabilidade
 *
 * Este é o caminho "Usar como Base" — converte blueprint em execução real.
 */
export async function createWorkflowInstanceFromBlueprint(params: {
  blueprintId: string;
  versionId?: string;
  organizationId: string;
  title: string;
  priority?: 'baixa' | 'media' | 'alta' | 'critica';
  initiatedBy?: string;
  contextData?: Record<string, any>;
}): Promise<BlueprintInstanceResult> {
  const {
    blueprintId,
    versionId,
    organizationId,
    title,
    priority = 'media',
    initiatedBy,
    contextData = {},
  } = params;

  if (!blueprintId) throw new Error('blueprintId é obrigatório');
  if (!organizationId) throw new Error('organizationId é obrigatório');
  if (!title?.trim()) throw new Error('title é obrigatório');

  console.info(`[createWorkflowInstanceFromBlueprint] Iniciando criação: blueprint=${blueprintId}, version=${versionId || 'latest'}`);
  structuredLogger.info('Criando instância a partir de blueprint', {
    operation: 'createWorkflowInstanceFromBlueprint',
    organizationId,
    blueprintId,
    versionId,
    userId: initiatedBy,
  });

  // 1. Carregar versão (snapshot)
  let version;
  if (versionId) {
    version = await getVersionById(versionId);
  } else {
    const versions = await listVersions(blueprintId);
    version = versions[0]; // listVersions já ordena por created_at desc
  }

  if (!version) {
    throw new Error('Versão do blueprint não encontrada ou snapshot inválido');
  }

  const snapshot = version.snapshotJson as any;
  if (!snapshot || !snapshot.stages || snapshot.stages.length === 0) {
    throw new Error('Snapshot da versão está vazio ou sem etapas configuradas');
  }

  const orderedSnapshotStages = [...snapshot.stages].sort((left: any, right: any) => {
    const leftOrder = Number(left?.order ?? 0);
    const rightOrder = Number(right?.order ?? 0);
    return leftOrder - rightOrder;
  });
  const stageRoleMap = await ensureWorkflowRoles(
    organizationId,
    orderedSnapshotStages.map((stage: any) => stage.role ?? stage.metadata?.responsavel ?? null),
  );
  const firstSnapshotStage = orderedSnapshotStages[0];
  const firstStageRoleLike = firstSnapshotStage?.role ?? firstSnapshotStage?.metadata?.responsavel ?? null;
  const firstStageRoleRecord = firstStageRoleLike
    ? stageRoleMap.get(normalizeWorkflowRoleKey(firstStageRoleLike) ?? '')
    : null;

  if (initiatedBy && (firstStageRoleRecord?.id || firstStageRoleLike)) {
    const canInitiate = await userHasWorkflowRole(
      initiatedBy,
      organizationId,
      firstStageRoleRecord?.id ?? null,
      firstStageRoleLike,
    );
    if (!canInitiate) {
      throw new Error('O usuario que inicia a execucao nao pertence ao papel exigido pela primeira etapa.');
    }
  }

  const versionSemver = version.versionSemver || '1.0.0';

  // 2. Garantir existência de template logicamente vinculado a ESTA VERSÃO do blueprint
  // Buscamos um template que já tenha sido gerado para este snapshot específico
  const { data: existingTemplate, error: searchError } = await supabase
    .from('workflow_templates_v9')
    .select('id')
    .eq('organization_id', organizationId)
    .contains('metadata', { blueprint_version_id: version.id })
    .limit(1)
    .maybeSingle();

  if (searchError) {
    console.error('[createWorkflowInstanceFromBlueprint] Erro ao buscar template:', searchError);
  }

  let templateId = existingTemplate?.id;

  if (!templateId) {
    console.info(`[createWorkflowInstanceFromBlueprint] Gerando novo template para versão ${versionSemver}`);
    const { data: newTemplate, error: templateError } = await supabase
      .from('workflow_templates_v9')
      .insert({
        organization_id: organizationId,
        name: `${snapshot.name || 'Template'} (v${versionSemver})`,
        description: `Gerado a partir do blueprint "${snapshot.name}" v${versionSemver}`,
        category: snapshot.category || 'processo',
        is_active: true,
        require_sequential_completion: true,
        auto_assign_strategy: 'manual',
        created_by: initiatedBy ?? null,
        metadata: {
          blueprint_id: blueprintId,
          blueprint_version_id: version.id,
          version_semver: versionSemver,
          is_snapshot: true
        },
      })
      .select('id')
      .single();

    let isNewTemplate = false;

    if (templateError) {
      // 409 = unique constraint violation: template with same name+org+version already exists
      // Fallback: find that existing template
      if ((templateError as any).code === '23505' || (templateError as any).status === 409) {
        const templateName = `${snapshot.name || 'Template'} (v${versionSemver})`;
        const { data: fallbackTemplate } = await supabase
          .from('workflow_templates_v9')
          .select('id')
          .eq('organization_id', organizationId)
          .eq('name', templateName)
          .limit(1)
          .maybeSingle();
        if (fallbackTemplate?.id) {
          templateId = fallbackTemplate.id;
        } else {
          console.error('[createWorkflowInstanceFromBlueprint] Erro ao criar template (sem fallback):', templateError);
          throw templateError;
        }
      } else {
        console.error('[createWorkflowInstanceFromBlueprint] Erro ao criar template:', templateError);
        throw templateError;
      }
    } else {
      templateId = newTemplate.id;
      isNewTemplate = true;
    }

    if (isNewTemplate) {
      // Criar stages no template a partir do snapshot
      const stageInserts = snapshot.stages.map((stage: any, index: number) => {
        const stageEngineering = resolveStageEngineering(stage);
        const roleLike = stage.role ?? stage.metadata?.responsavel ?? null;
        const roleRecord = roleLike
          ? stageRoleMap.get(normalizeWorkflowRoleKey(roleLike) ?? '')
          : null;
        return {
          template_id: templateId!,
          organization_id: organizationId,
          name: stage.name || `Etapa ${index + 1}`,
          description: stage.description ?? null,
          stage_order: Math.max(1, stage.order || index + 1), // Garante ordem > 0
          sla_hours: typeof stage.slaHours === 'number' ? stage.slaHours : null,
          sla_business_hours: false,
          warning_threshold_percent: stageEngineering.warningThresholdPercent,
          require_approval: stageEngineering.requireApproval,
          can_skip: false,
          responsible_role_id: roleRecord?.id ?? null,
          default_assignee_role: roleLike,
          wip_limit: stageEngineering.wipLimit,
          metadata: {
            blueprint_id: blueprintId,
            version_id: version.id,
            checklist: stage.checklist ?? [],
            formFields: stage.formFields ?? [],
            instruction: stage.instruction ?? null,
            completionType: stage.completionType ?? 'simple_complete',
            rules: stage.rules ?? {},
            executionConfig: stage.executionConfig ?? null,
            tp_standard: stageEngineering.tpStandard,
            tra_standard: stageEngineering.traStandard,
            expected_error_percent: stageEngineering.expectedErrorPercent,
            correction_time_standard: stageEngineering.correctionTimeStandard,
            warning_threshold_percent: stageEngineering.warningThresholdPercent,
            wip_limit: stageEngineering.wipLimit,
            require_approval: stageEngineering.requireApproval,
            role: roleRecord?.roleKey ?? roleLike ?? null,
            roleId: roleRecord?.id ?? null,
            roleName: roleRecord?.name ?? humanizeWorkflowRoleName(roleLike) ?? null,
          },
        };
      });

      const { error: stagesError } = await supabase.from('workflow_stages_v9').insert(stageInserts);
      
      if (stagesError) {
        console.error('[createWorkflowInstanceFromBlueprint] Erro ao inserir etapas:', stagesError);
        throw new Error(`Erro ao configurar etapas do template: ${stagesError.message}`);
      }
    }
    
    // Atualizar o bpRecord apenas se for a versão mais recente? 
    // Por simplicidade, mantemos o generated_template_id apontando para o último gerado
    await updateBlueprint(blueprintId, { generatedTemplateId: templateId });
  }

  // 3. Obter Stages do template e garantir que ele não está vazio
  const { data: stages, error: stagesSearchError } = await supabase
    .from('workflow_stages_v9')
    .select('id, name, stage_order')
    .eq('template_id', templateId)
    .eq('organization_id', organizationId)
    .order('stage_order', { ascending: true });

  if (stagesSearchError) {
    console.error('[createWorkflowInstanceFromBlueprint] Erro ao buscar estágios:', stagesSearchError);
    throw stagesSearchError;
  }

  let firstStageId: string;

  // Se o template existe mas as etapas não foram encontradas, tentar recriar a partir do snapshot
  if (!stages || stages.length === 0) {
    console.warn(`[createWorkflowInstanceFromBlueprint] Template ${templateId} encontrado mas está sem etapas. Tentando recriar...`);
    
    const stageInserts = snapshot.stages.map((stage: any, index: number) => {
      const stageEngineering = resolveStageEngineering(stage);
      const roleLike = stage.role ?? stage.metadata?.responsavel ?? null;
      const roleRecord = roleLike
        ? stageRoleMap.get(normalizeWorkflowRoleKey(roleLike) ?? '')
        : null;
      return {
        template_id: templateId!,
        organization_id: organizationId,
        name: stage.name || `Etapa ${index + 1}`,
        description: stage.description ?? null,
        stage_order: Math.max(1, stage.order || index + 1),
        sla_hours: typeof stage.slaHours === 'number' ? stage.slaHours : null,
        sla_business_hours: false,
        warning_threshold_percent: stageEngineering.warningThresholdPercent,
        require_approval: stageEngineering.requireApproval,
        can_skip: false,
        responsible_role_id: roleRecord?.id ?? null,
        default_assignee_role: roleLike,
        wip_limit: stageEngineering.wipLimit,
        metadata: {
          blueprint_id: blueprintId,
          version_id: version.id,
          checklist: stage.checklist ?? [],
          formFields: stage.formFields ?? [],
          instruction: stage.instruction ?? null,
          completionType: stage.completionType ?? 'simple_complete',
          rules: stage.rules ?? {},
          executionConfig: stage.executionConfig ?? null,
          tp_standard: stageEngineering.tpStandard,
          tra_standard: stageEngineering.traStandard,
          expected_error_percent: stageEngineering.expectedErrorPercent,
          correction_time_standard: stageEngineering.correctionTimeStandard,
          warning_threshold_percent: stageEngineering.warningThresholdPercent,
          wip_limit: stageEngineering.wipLimit,
          require_approval: stageEngineering.requireApproval,
          role: roleRecord?.roleKey ?? roleLike ?? null,
          roleId: roleRecord?.id ?? null,
          roleName: roleRecord?.name ?? humanizeWorkflowRoleName(roleLike) ?? null,
        },
      };
    });

    const { data: insertedStages, error: reinsertError } = await supabase
      .from('workflow_stages_v9')
      .insert(stageInserts)
      .select('id');

    if (reinsertError) {
      console.error('[createWorkflowInstanceFromBlueprint] Erro ao recriar etapas (Fallback):', reinsertError);
      throw new Error(`O modelo existe mas está sem etapas e falhou ao recriar: ${reinsertError.message}`);
    }
    
    if (!insertedStages || insertedStages.length === 0) {
      throw new Error('Falha ao recriar etapas para o modelo existente. Verifique o Blueprint.');
    }
    
    // Pegar o ID da primeira etapa (menor stage_order) do snapshot
    firstStageId = insertedStages[0].id;
    console.log(`[createWorkflowInstanceFromBlueprint] Etapas recriadas com sucesso. Primeira etapa: ${firstStageId}`);
  } else {
    firstStageId = stages[0].id;
  }

  const referenceCode = `EXEC-${Date.now().toString().slice(-6)}`;

  // 5. Criar instância vinculada à versão e blueprint
  // Incluir snapshot_hash na instância para rastreabilidade completa
  const instanceSnapshotHash: string | undefined = version.snapshotHash ?? undefined;

  const { data: instanceRow, error: instanceError } = await supabase
    .from('workflow_instances_v9')
    .insert({
      template_id: templateId,
      organization_id: organizationId,
      reference_code: referenceCode,
      title: title.trim(),
      status: 'active',
      priority,
      current_stage_id: firstStageId,
      initiated_by: initiatedBy ?? null,
      started_at: new Date().toISOString(),
      blueprint_id: blueprintId,
      blueprint_version_id: version.id,
      context_data: {
        ...contextData,
        blueprint_id: blueprintId,
        blueprint_version_id: version.id,
        version_semver: versionSemver,
        snapshot_hash: instanceSnapshotHash,
      },
      metadata: {
        priority,
        blueprint_id: blueprintId,
        blueprint_version_id: version.id,
        version_semver: versionSemver,
        snapshot_hash: instanceSnapshotHash,
        created_from: 'blueprint_execution'
      },
    })
    .select('id')
    .single();

  if (instanceError) {
    structuredLogger.error('Falha ao criar instância de workflow', {
      operation: 'createWorkflowInstanceFromBlueprint',
      organizationId,
      blueprintId,
      versionId: version.id,
      userId: initiatedBy,
    }, instanceError);
    throw instanceError;
  }

  // 6. Criar tasks a partir do template com metadados da versão
  const { data: allStages } = await supabase
    .from('workflow_stages_v9')
    .select('*')
    .eq('template_id', templateId)
    .order('stage_order', { ascending: true });

  const taskInserts = (allStages || []).map((stage: any, idx: number) => ({
    instance_id: instanceRow.id,
    stage_id: stage.id,
    organization_id: organizationId,
    status: idx === 0 ? 'pending' : 'waiting',
    assigned_role_id: stage.responsible_role_id ?? null,
    assigned_department_id: stage.department_id ?? stage.metadata?.department_id ?? null,
    assigned_user_id: null,
    assigned_to: null,
    claimed_at: null,
    stage_name: stage.name,
    metadata: {
      ...stage.metadata,
      version: versionSemver,
      blueprint_version_id: version.id,
      role: stage.metadata?.role ?? stage.default_assignee_role ?? null,
      roleId: stage.responsible_role_id ?? stage.metadata?.roleId ?? null,
      roleName: stage.metadata?.roleName ?? humanizeWorkflowRoleName(stage.default_assignee_role) ?? null,
      role_snapshot: {
        role_id: stage.responsible_role_id ?? stage.metadata?.roleId ?? null,
        role_name: stage.metadata?.roleName ?? stage.default_assignee_role ?? null,
      },
      department_snapshot: {
        department_id: stage.department_id ?? stage.metadata?.department_id ?? null,
        department_name: stage.metadata?.department_name ?? null,
      },
      operationalMetrics: idx === 0
        ? {
            ...((stage.metadata?.operationalMetrics as Record<string, unknown> | undefined) ?? {}),
            enteredAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }
        : ((stage.metadata?.operationalMetrics as Record<string, unknown> | undefined) ?? undefined),
      operationalHistory: idx === 0
        ? [
            ...(((stage.metadata?.operationalHistory as unknown[]) ?? []) as unknown[]),
            {
              event: 'activated',
              capturedAt: new Date().toISOString(),
              enteredAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              result: null,
            },
          ]
        : ((stage.metadata?.operationalHistory as unknown[]) ?? undefined),
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  const { error: tasksError } = await supabase.from('workflow_tasks_v9').insert(taskInserts);
  if (tasksError) console.error('[createWorkflowInstanceFromBlueprint] Erro ao criar tasks:', tasksError);

  // 7. Registrar uso
  await supabase.from('process_blueprint_usage').insert({
    blueprint_id: blueprintId,
    organization_id: organizationId,
    blueprint_version: version.version,
    blueprint_semver: versionSemver
  });

  // 8. Log Final de Governança
  logGovernanceAction({
    blueprintId,
    organizationId,
    action: 'instance_created',
    performedBy: initiatedBy,
    details: { instanceId: instanceRow.id, version_id: version.id, versionSemver }
  });

  return {
    instanceId: instanceRow.id,
    templateId,
    referenceCode,
    tasksCreated: taskInserts.length,
    status: 'active' as const,
    blueprintId,
    versionSemver
  };
}
